import React, { useState } from 'react';
import {
  Container,
  Grid,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Chip,
  Avatar,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineDot,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent
} from '@mui/lab';
import { styled, alpha } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  TrackChanges as TrackIcon,
  Diamond as DiamondIcon
} from '@mui/icons-material';
import { useCart } from '../context/CartContext';
// import './OrdersPage.css';

const StyledTab = styled(Tab)({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1rem',
  '&.Mui-selected': {
    color: '#C6A43F',
  }
});

const OrderCard = styled(motion(Paper))({
  padding: '24px',
  marginBottom: '16px',
  borderRadius: '16px',
  border: '1px solid rgba(198, 164, 63, 0.2)',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: '#C6A43F',
    boxShadow: '0 10px 30px rgba(198, 164, 63, 0.1)',
    transform: 'translateY(-2px)'
  }
});

const StatusStepper = styled(Stepper)({
  '& .MuiStepLabel-label': {
    fontSize: '0.8rem',
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: '#C6A43F',
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: '#4CAF50',
  }
});

const OrdersPage = () => {
  const navigate = useNavigate();
  const { orders, orderHistory } = useCart();
  const [tabValue, setTabValue] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [trackingOpen, setTrackingOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const activeOrders = orders || [];
  const completedOrders = orderHistory || [];

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return '#4CAF50';
      case 'processing': return '#2196F3';
      case 'shipped': return '#FF9800';
      case 'cancelled': return '#f44336';
      default: return '#9E9E9E';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircleIcon />;
      case 'processing': return <PendingIcon />;
      case 'shipped': return <ShippingIcon />;
      case 'cancelled': return <CancelIcon />;
      default: return <ShoppingBagIcon />;
    }
  };

  const getOrderSteps = (status) => {
    const steps = ['Order Placed', 'Confirmed', 'Processing', 'Shipped', 'Out for Delivery', 'Delivered'];
    let activeStep = 0;
    
    switch (status) {
      case 'confirmed': activeStep = 1; break;
      case 'processing': activeStep = 2; break;
      case 'shipped': activeStep = 3; break;
      case 'out-for-delivery': activeStep = 4; break;
      case 'delivered': activeStep = 5; break;
      default: activeStep = 0;
    }
    
    return { steps, activeStep };
  };

  const handleTrackOrder = (order) => {
    setSelectedOrder(order);
    setTrackingOpen(true);
  };

  const handleCancelOrder = (orderId) => {
    // Cancel order logic
    setSnackbar({
      open: true,
      message: 'Order cancelled successfully',
      severity: 'success'
    });
  };

  const handleReorder = (items) => {
    // Add all items to cart
    navigate('/cart');
    setSnackbar({
      open: true,
      message: 'Items added to cart',
      severity: 'success'
    });
  };

  const OrderTrackingModal = ({ order, onClose }) => {
    const { steps, activeStep } = getOrderSteps(order.status);

    // Sample timeline data
    const timelineData = [
      { time: '10:30 AM', date: 'Feb 13', title: 'Order Placed', description: 'Your order has been placed successfully' },
      { time: '02:30 PM', date: 'Feb 13', title: 'Order Confirmed', description: 'Your order has been confirmed' },
      { time: '09:00 AM', date: 'Feb 14', title: 'Processing', description: 'Your jewelry is being crafted' },
      { time: '05:30 PM', date: 'Feb 15', title: 'Shipped', description: 'Your order has been shipped' },
      { time: 'Expected by', date: 'Feb 16', title: 'Out for Delivery', description: 'Your package is out for delivery' },
    ];

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(10, 15, 30, 0.95)',
          backdropFilter: 'blur(10px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8, y: 50 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.8, y: -50 }}
          transition={{ type: "spring", damping: 25 }}
          style={{ width: '90%', maxWidth: 800, maxHeight: '90vh', overflow: 'auto' }}
          onClick={(e) => e.stopPropagation()}
        >
          <Paper sx={{ p: 4, borderRadius: '24px', background: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h4" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 700, color: '#0A0F1E' }}>
                Track Order #{order.id}
              </Typography>
              <Button onClick={onClose} sx={{ color: '#C6A43F' }}>
                Close
              </Button>
            </Box>

            {/* Order Status Stepper */}
            <StatusStepper activeStep={activeStep} alternativeLabel sx={{ mb: 6 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </StatusStepper>

            {/* Timeline */}
            <Typography variant="h6" sx={{ mb: 3, color: '#C6A43F' }}>
              Tracking Timeline
            </Typography>
            
            <Timeline position="alternate">
              {timelineData.map((item, index) => (
                <TimelineItem key={index}>
                  <TimelineOppositeContent color="text.secondary">
                    <Typography variant="body2">{item.time}</Typography>
                    <Typography variant="caption">{item.date}</Typography>
                  </TimelineOppositeContent>
                  <TimelineSeparator>
                    <TimelineDot sx={{ 
                      bgcolor: index <= activeStep ? '#C6A43F' : '#ccc',
                      boxShadow: index <= activeStep ? '0 0 0 4px rgba(198,164,63,0.2)' : 'none'
                    }}>
                      {index === 0 && <ShoppingBagIcon fontSize="small" sx={{ color: 'white' }} />}
                      {index === 1 && <CheckCircleIcon fontSize="small" sx={{ color: 'white' }} />}
                      {index === 2 && <DiamondIcon fontSize="small" sx={{ color: 'white' }} />}
                      {index === 3 && <ShippingIcon fontSize="small" sx={{ color: 'white' }} />}
                      {index === 4 && <TrackIcon fontSize="small" sx={{ color: 'white' }} />}
                    </TimelineDot>
                    {index < timelineData.length - 1 && <TimelineConnector sx={{ bgcolor: index < activeStep ? '#C6A43F' : '#ccc' }} />}
                  </TimelineSeparator>
                  <TimelineContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>

            {/* Delivery Address */}
            <Box sx={{ mt: 4, p: 3, bgcolor: alpha('#C6A43F', 0.05), borderRadius: '12px' }}>
              <Typography variant="subtitle2" sx={{ mb: 1, color: '#C6A43F' }}>
                Delivery Address
              </Typography>
              <Typography variant="body2">
                {order.shippingDetails?.fullName || 'Customer Name'}<br />
                {order.shippingDetails?.addressLine1 || '123, Jewel Street'}<br />
                {order.shippingDetails?.city || 'Mumbai'}, {order.shippingDetails?.state || 'Maharashtra'} - {order.shippingDetails?.pincode || '400001'}<br />
                Phone: {order.shippingDetails?.phone || '+91 9876543210'}
              </Typography>
            </Box>

            {/* Estimated Delivery */}
            {order.estimatedDelivery && (
              <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <ShippingIcon sx={{ color: '#C6A43F' }} />
                <Typography>
                  Estimated Delivery: <strong>{new Date(order.estimatedDelivery).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                </Typography>
              </Box>
            )}
          </Paper>
        </motion.div>
      </motion.div>
    );
  };

  return (
    <Box className="orders-page" sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #FAF7F2 0%, #FFFFFF 100%)' }}>
      <Container maxWidth="lg" sx={{ py: 12 }}>
        {/* Header */}
        <motion.div
          initial={{ y: -30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontFamily: 'Cormorant Garamond', 
                fontWeight: 700, 
                color: '#0A0F1E',
                mb: 2,
                position: 'relative',
                display: 'inline-block',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 80,
                  height: 3,
                  background: 'linear-gradient(90deg, #C6A43F, #E5D5AC)',
                  borderRadius: 2,
                }
              }}
            >
              My Orders
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track and manage your jewelry orders
            </Typography>
          </Box>
        </motion.div>

        {/* Tabs */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <Paper sx={{ mb: 4, borderRadius: '12px', overflow: 'hidden' }}>
            <Tabs
              value={tabValue}
              onChange={(e, v) => setTabValue(v)}
              variant="fullWidth"
              sx={{
                bgcolor: 'white',
                '& .MuiTabs-indicator': {
                  backgroundColor: '#C6A43F',
                  height: 3,
                }
              }}
            >
              <StyledTab 
                label={`Active Orders (${activeOrders.length})`} 
                icon={<ShoppingBagIcon />} 
                iconPosition="start"
              />
              <StyledTab 
                label={`Order History (${completedOrders.length})`} 
                icon={<ReceiptIcon />} 
                iconPosition="start"
              />
            </Tabs>
          </Paper>
        </motion.div>

        {/* Orders List */}
        <AnimatePresence mode="wait">
          {tabValue === 0 ? (
            <motion.div
              key="active"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              {activeOrders.length === 0 ? (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '16px' }}>
                    <motion.div
                      animate={{ 
                        scale: [1, 1.1, 1],
                        rotate: [0, 5, -5, 0]
                      }}
                      transition={{ duration: 4, repeat: Infinity }}
                    >
                      <ShoppingBagIcon sx={{ fontSize: 100, color: 'rgba(198,164,63,0.2)', mb: 3 }} />
                    </motion.div>
                    <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', mb: 2 }}>
                      No Active Orders
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                      Start shopping to see your active orders here
                    </Typography>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/shop')}
                      sx={{
                        bgcolor: '#C6A43F',
                        color: '#0A0F1E',
                        px: 6,
                        py: 2,
                        '&:hover': { bgcolor: '#B3923A' }
                      }}
                    >
                      Shop Now
                    </Button>
                  </Paper>
                </motion.div>
              ) : (
                activeOrders.map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <OrderCard>
                      <Grid container spacing={3}>
                        {/* Order Header */}
                        <Grid item xs={12}>
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2
                          }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                                Order #{order.id}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Placed on {new Date(order.orderDate).toLocaleDateString('en-IN', { 
                                  day: 'numeric', 
                                  month: 'long', 
                                  year: 'numeric' 
                                })}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Chip
                                icon={getStatusIcon(order.status)}
                                label={order.status.toUpperCase()}
                                sx={{
                                  bgcolor: alpha(getStatusColor(order.status), 0.1),
                                  color: getStatusColor(order.status),
                                  fontWeight: 600,
                                  px: 1
                                }}
                              />
                              {order.status === 'processing' && (
                                <Chip
                                  label="Cancel"
                                  onClick={() => handleCancelOrder(order.id)}
                                  sx={{
                                    bgcolor: alpha('#f44336', 0.1),
                                    color: '#f44336',
                                    fontWeight: 600,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: alpha('#f44336', 0.2),
                                    }
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Grid>

                        {/* Order Items */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ maxHeight: 200, overflow: 'auto', pr: 1 }}>
                            {order.items?.map((item) => (
                              <Box 
                                key={item.id} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  gap: 2, 
                                  mb: 2,
                                  p: 1,
                                  borderRadius: '8px',
                                  '&:hover': {
                                    bgcolor: alpha('#C6A43F', 0.05)
                                  }
                                }}
                              >
                                <Avatar
                                  src={item.images?.[0]}
                                  variant="rounded"
                                  sx={{ 
                                    width: 70, 
                                    height: 70,
                                    border: '1px solid rgba(198,164,63,0.2)'
                                  }}
                                >
                                  <DiamondIcon sx={{ color: '#C6A43F' }} />
                                </Avatar>
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {item.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {item.metalPurity} · {item.category}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    ₹{item.finalPrice?.toLocaleString()}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Qty: {item.quantity}
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        </Grid>

                        {/* Order Footer */}
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 2
                          }}>
                            <Box>
                              <Typography variant="body2" color="text.secondary">
                                Total Amount
                              </Typography>
                              <Typography variant="h5" sx={{ color: '#C6A43F', fontWeight: 700 }}>
                                ₹{order.totalAmount?.toLocaleString()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Button
                                variant="outlined"
                                startIcon={<TrackIcon />}
                                onClick={() => handleTrackOrder(order)}
                                sx={{ 
                                  borderColor: '#C6A43F', 
                                  color: '#C6A43F',
                                  '&:hover': {
                                    borderColor: '#B3923A',
                                    backgroundColor: 'rgba(198,164,63,0.04)'
                                  }
                                }}
                              >
                                Track Order
                              </Button>
                              <Button
                                variant="contained"
                                startIcon={<VisibilityIcon />}
                                sx={{ 
                                  bgcolor: '#0A0F1E',
                                  '&:hover': { 
                                    bgcolor: '#C6A43F',
                                  }
                                }}
                              >
                                Details
                              </Button>
                            </Box>
                          </Box>

                          {/* Estimated Delivery */}
                          {order.estimatedDelivery && (
                            <Box sx={{ 
                              mt: 3, 
                              p: 2, 
                              bgcolor: alpha('#C6A43F', 0.05), 
                              borderRadius: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2
                            }}>
                              <ShippingIcon sx={{ color: '#C6A43F' }} />
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Estimated Delivery
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {new Date(order.estimatedDelivery).toLocaleDateString('en-IN', { 
                                    weekday: 'long',
                                    day: 'numeric', 
                                    month: 'long', 
                                    year: 'numeric' 
                                  })}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </Grid>
                      </Grid>
                    </OrderCard>
                  </motion.div>
                ))
              )}
            </motion.div>
          ) : (
            <motion.div
              key="history"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.4 }}
            >
              {completedOrders.length === 0 ? (
                <Paper sx={{ p: 8, textAlign: 'center', borderRadius: '16px' }}>
                  <ReceiptIcon sx={{ fontSize: 80, color: 'rgba(198,164,63,0.2)', mb: 2 }} />
                  <Typography variant="h5" sx={{ fontFamily: 'Cormorant Garamond', mb: 2 }}>
                    No Order History
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    Your completed orders will appear here
                  </Typography>
                </Paper>
              ) : (
                completedOrders.map((order, index) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <OrderCard>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="h6" sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                                Order #{order.id}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                Delivered on {new Date(order.deliveryDate || order.orderDate).toLocaleDateString('en-IN', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric'
                                })}
                              </Typography>
                            </Box>
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="DELIVERED"
                              sx={{ 
                                bgcolor: alpha('#4CAF50', 0.1), 
                                color: '#4CAF50', 
                                fontWeight: 600,
                                px: 1
                              }}
                            />
                          </Box>
                        </Grid>

                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h5" sx={{ color: '#C6A43F', fontWeight: 700 }}>
                              ₹{order.totalAmount?.toLocaleString()}
                            </Typography>
                            <Button
                              variant="outlined"
                              onClick={() => handleReorder(order.items)}
                              sx={{ 
                                borderColor: '#C6A43F', 
                                color: '#C6A43F',
                                '&:hover': {
                                  borderColor: '#B3923A',
                                  bgcolor: 'rgba(198,164,63,0.04)'
                                }
                              }}
                            >
                              Buy Again
                            </Button>
                          </Box>
                        </Grid>
                      </Grid>
                    </OrderCard>
                  </motion.div>
                ))
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </Container>

      {/* Tracking Modal */}
      <AnimatePresence>
        {trackingOpen && selectedOrder && (
          <OrderTrackingModal
            order={selectedOrder}
            onClose={() => setTrackingOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          severity={snackbar.severity}
          sx={{
            borderRadius: '8px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OrdersPage;